<template>
    <main class="smartdata-landing">
        <section class="landing-hero" aria-labelledby="smartdata-landing-title">
            <div class="landing-hero__backdrop" aria-hidden="true"></div>
            <div class="landing-hero__inner">
                <div class="landing-copy">
                    <img
                        src="/static/img/logos/Smartdata_dark_L.png"
                        alt="Smartdata"
                        class="landing-copy__logo"
                    />

                    <h1
                        id="smartdata-landing-title"
                        class="landing-copy__title"
                    >
                        Aerial intelligence, ready when you log in
                    </h1>
                    <p class="landing-copy__body">
                        Bring inspection imagery, maps, 3D models, point clouds,
                        and AI review into one clear workspace.
                    </p>

                    <div class="landing-actions">
                        <v-btn
                            color="primary"
                            href="#smartdata-capabilities"
                            class="landing-action landing-action--primary"
                        >
                            Explore the platform
                            <v-icon end icon="mdi-arrow-right"></v-icon>
                        </v-btn>
                        <a
                            class="landing-action-link"
                            href="/accounts/register/"
                        >
                            Need access? Create account
                            <v-icon
                                size="small"
                                icon="mdi-chevron-right"
                            ></v-icon>
                        </a>
                    </div>
                </div>

                <section
                    class="landing-login-panel"
                    aria-labelledby="landing-login-title"
                >
                    <h2
                        id="landing-login-title"
                        class="landing-login-panel__title"
                    >
                        Log in to Smartdata
                    </h2>

                    <v-form
                        ref="form"
                        v-model="login.formValid"
                        @submit.prevent="submitLogin"
                    >
                        <v-alert
                            v-if="emailVerificationSuccess"
                            variant="outlined"
                            type="success"
                            class="landing-alert"
                        >
                            Your email address has been successfully verified.
                            You may now login.
                        </v-alert>

                        <v-alert
                            v-if="emailVerificationError"
                            variant="outlined"
                            type="error"
                            class="landing-alert"
                        >
                            There was an error verifying your email address.
                            Please try again.
                        </v-alert>

                        <v-alert
                            v-if="login.loginInvalidCredentials"
                            variant="outlined"
                            type="error"
                            class="landing-alert"
                        >
                            {{ login.invalidCredentialsMsg }}
                        </v-alert>

                        <v-alert
                            v-if="login.genericError"
                            variant="outlined"
                            type="error"
                            class="landing-alert"
                        >
                            Unknown error while trying to login.
                        </v-alert>

                        <v-alert
                            v-if="login.unverifiedEmail"
                            variant="outlined"
                            type="warning"
                            class="landing-alert"
                        >
                            Email is not verified, you must verify your email
                            before you can login.
                        </v-alert>

                        <v-btn
                            v-if="login.unverifiedEmail"
                            color="primary"
                            variant="text"
                            class="landing-resend"
                            @click="resendEmail"
                        >
                            Resend Email
                        </v-btn>

                        <v-text-field
                            id="email-field"
                            v-model="login.email"
                            autofocus
                            class="landing-input"
                            label="Email address"
                            prepend-inner-icon="$vuetify.icons.email"
                            :rules="[rules.required, rules.email]"
                            variant="outlined"
                            type="email"
                            name="email"
                            autocomplete="email"
                        />

                        <v-text-field
                            id="password-field"
                            v-model="login.password"
                            class="landing-input"
                            prepend-inner-icon="$vuetify.icons.lock"
                            :append-inner-icon="
                                login.showPassword
                                    ? '$vuetify.icons.eye'
                                    : '$vuetify.icons.eyeSlash'
                            "
                            :type="login.showPassword ? 'text' : 'password'"
                            name="password"
                            label="Password"
                            :rules="[rules.required]"
                            variant="outlined"
                            autocomplete="current-password"
                            @click:append-inner="
                                login.showPassword = !login.showPassword
                            "
                        />

                        <v-btn
                            type="submit"
                            size="large"
                            color="primary"
                            :loading="loader"
                            class="landing-submit"
                            block
                        >
                            Log in
                        </v-btn>
                    </v-form>

                    <div class="landing-login-links">
                        <a href="/accounts/password/reset/request/"
                            >Forgot password?</a
                        >
                        <a href="/accounts/register/">Create account</a>
                    </div>
                </section>
            </div>
        </section>

        <section
            id="smartdata-capabilities"
            class="landing-capabilities"
            aria-label="Smartdata capabilities"
        >
            <article
                v-for="capability in capabilities"
                :key="capability.title"
                class="landing-capability"
            >
                <div class="landing-capability__icon" aria-hidden="true">
                    <v-icon :icon="capability.icon"></v-icon>
                </div>
                <div>
                    <h2 class="landing-capability__title">
                        {{ capability.title }}
                    </h2>
                    <p class="landing-capability__body">
                        {{ capability.body }}
                    </p>
                </div>
            </article>
        </section>

        <generic-snack-bar ref="snackbar" />
        <two-factor-authentication
            ref="twoFactorDialog"
            @token-ready="completeTwoFactorLogin"
        />
    </main>
</template>

<script>
import axios from 'axios';
import GenericSnackBar from 'components/GenericSnackbar/GenericSnackBar.vue';
import TwoFactorAuthentication from 'components/Profile/PhoneNumber/LoginPhoneVerification.vue';

export default {
    name: 'SmartdataLanding',
    components: { GenericSnackBar, TwoFactorAuthentication },
    props: {
        emailVerification: {
            type: [String, Boolean],
            default: '',
        },
    },
    data() {
        return {
            capabilities: [
                {
                    title: 'Inspection AI',
                    body: 'Automatically detect issues, prioritise risks and accelerate field decisions with AI review.',
                    icon: 'mdi-crosshairs-gps',
                },
                {
                    title: '3D & point cloud',
                    body: 'Visualise assets in true detail with 3D models and point clouds for accurate analysis.',
                    icon: 'mdi-cube-outline',
                },
                {
                    title: 'Team delivery',
                    body: 'Coordinate work, share insights and deliver outcomes across your organisation.',
                    icon: 'mdi-account-group-outline',
                },
            ],
            rules: {
                required: (value) => !!value || 'Required field',
                email: (value) => {
                    const pattern =
                        /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\d-A-Za-z]+\.)+[A-Za-z]{2,}))$/;
                    return pattern.test(value) || 'Must be a valid e-mail';
                },
            },
            login: {
                email: null,
                password: null,
                showPassword: false,
                formValid: null,
                loginSuccess: false,
                loginInvalidCredentials: false,
                genericError: false,
                unverifiedEmail: false,
                resendEmailLink: null,
                invalidCredentialsMsg: 'Incorrect email or password.',
            },
            emailVerificationSuccess: false,
            emailVerificationError: false,
            authyId: '',
            loader: false,
        };
    },
    mounted() {
        if (String(this.emailVerification) === 'True') {
            this.emailVerificationSuccess = true;
        } else if (String(this.emailVerification) === 'False') {
            this.emailVerificationError = true;
        }
    },
    methods: {
        async validateForm() {
            const validationResult = await this.$refs.form.validate();
            if (typeof validationResult === 'boolean') {
                return validationResult;
            }
            if (
                validationResult &&
                typeof validationResult === 'object' &&
                'valid' in validationResult
            ) {
                return Boolean(validationResult.valid);
            }
            return false;
        },
        completeTwoFactorLogin(authyToken) {
            this.$refs.twoFactorDialog.close();
            this.loader = true;

            axios
                .post('/accounts/login/', {
                    email: this.login.email,
                    password: this.login.password,
                    token: authyToken,
                })
                .then((response) => {
                    this.loader = false;
                    if (response.data.twoFactorFailed) {
                        this.$refs.twoFactorDialog.init(authyToken, true);
                    } else if (response.data.success) {
                        this.loginSuccess();
                    }
                })
                .catch((error) => {
                    this.loader = false;
                    this.login.genericError = true;
                    throw error;
                });
        },
        loginSuccess() {
            this.login.loginSuccess = true;
            const urlParameters = new URLSearchParams(window.location.search);
            const redirectTo = urlParameters.get('next');
            document.location = redirectTo || '/';
        },
        async submitLogin() {
            const isValid = await this.validateForm();
            if (!isValid) return;

            this.loader = true;
            this.turnOffMessages();
            axios
                .post('/accounts/login/', {
                    email: this.login.email,
                    password: this.login.password,
                })
                .then((response) => {
                    this.loader = false;
                    if (response.data.success === true) {
                        this.loginSuccess();
                    } else if (response.data.unverifiedEmail) {
                        this.login.unverifiedEmail = true;
                        this.login.resendEmailLink =
                            response.data.resendEmailLink;
                    } else if (response.data.loginInvalidCredentials) {
                        this.login.loginInvalidCredentials = true;
                        this.login.invalidCredentialsMsg =
                            response.data.message;
                    } else if (response.data.twoFactorRequired) {
                        this.authyId = response.data.authyId;
                        this.$refs.twoFactorDialog.init(this.authyId);
                    }
                })
                .catch((error) => {
                    this.loader = false;
                    this.login.genericError = true;
                    throw error;
                });
        },
        resendEmail() {
            axios
                .get(this.login.resendEmailLink)
                .then((response) => response.data)
                .then((data) => {
                    this.$refs.snackbar.show(
                        data.success
                            ? 'The verification email has been resent.'
                            : 'There was an error resending the email.'
                    );
                })
                .catch((error) => {
                    this.$refs.snackbar.show(
                        'There was an error resending the email.'
                    );
                    throw error;
                });
        },
        turnOffMessages() {
            this.login.loginInvalidCredentials = false;
            this.emailVerificationError = false;
            this.emailVerificationSuccess = false;
            this.login.loginSuccess = false;
            this.login.unverifiedEmail = false;
            this.login.genericError = false;
        },
    },
};
</script>

<style scoped>
.smartdata-landing {
    width: 100%;
    min-height: calc(100vh - 72px);
    overflow: hidden;
    background: #ffffff;
    color: #07132e;
    font-family: var(--sd-font-primary, Inter, sans-serif);
}

.landing-hero {
    position: relative;
    min-height: clamp(610px, calc(100svh - 92px), 760px);
    isolation: isolate;
    border-bottom: 1px solid rgba(101, 117, 143, 0.18);
}

.landing-hero__backdrop {
    position: absolute;
    inset: 0;
    z-index: -1;
    background-image:
        linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.98) 0%,
            rgba(255, 255, 255, 0.91) 35%,
            rgba(255, 255, 255, 0.54) 58%,
            rgba(255, 255, 255, 0.18) 100%
        ),
        url('/static/img/landing/smartdata-landing-hero.png');
    background-position: center right;
    background-size: cover;
}

.landing-hero__backdrop::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0) 72%, #ffffff 100%),
        repeating-linear-gradient(
            135deg,
            rgba(60, 100, 244, 0.06) 0,
            rgba(60, 100, 244, 0.06) 1px,
            transparent 1px,
            transparent 22px
        );
    opacity: 0.4;
}

.landing-hero__inner {
    display: grid;
    grid-template-columns: minmax(0, 0.94fr) minmax(360px, 500px);
    gap: clamp(32px, 6vw, 92px);
    align-items: center;
    width: min(100% - 48px, 1360px);
    min-height: inherit;
    margin: 0 auto;
    padding: clamp(34px, 5vw, 72px) 0 34px;
}

.landing-copy {
    max-width: 650px;
}

.landing-copy__logo {
    display: block;
    width: min(252px, 58vw);
    height: auto;
    margin: 0 0 clamp(32px, 4vw, 48px);
}

.landing-copy__title {
    max-width: 650px;
    margin: 0;
    color: #07132e;
    font-size: clamp(3rem, 5vw, 4.8rem);
    font-weight: 800;
    line-height: 0.98;
    letter-spacing: 0;
}

.landing-copy__body {
    max-width: 560px;
    margin: 26px 0 0;
    color: #4b5872;
    font-size: clamp(1.16rem, 1.8vw, 1.55rem);
    line-height: 1.55;
}

.landing-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 22px 26px;
    margin-top: 34px;
}

.landing-action {
    min-height: 56px;
    border-radius: 8px !important;
    padding: 0 28px !important;
    text-transform: none;
    letter-spacing: 0;
    font-size: 1rem !important;
    font-weight: 700;
    box-shadow: 0 18px 32px rgba(13, 94, 219, 0.22);
}

.landing-action-link {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: #045edb;
    font-size: 1rem;
    font-weight: 700;
    text-decoration: none;
}

.landing-action-link:hover,
.landing-login-links a:hover {
    text-decoration: underline;
}

.landing-login-panel {
    position: relative;
    width: 100%;
    max-width: 500px;
    margin-left: auto;
    padding: clamp(30px, 4vw, 44px);
    border: 1px solid rgba(101, 117, 143, 0.22);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 28px 70px rgba(10, 25, 57, 0.18);
    backdrop-filter: blur(12px);
}

.landing-login-panel__title {
    margin: 0 0 28px;
    color: #07132e;
    font-size: clamp(1.55rem, 2.1vw, 2rem);
    font-weight: 750;
    line-height: 1.2;
    letter-spacing: 0;
}

.landing-alert {
    margin-bottom: 14px;
}

.landing-resend {
    margin: -2px 0 12px;
    padding-inline: 0 !important;
    text-transform: none;
}

.landing-input {
    margin-bottom: 12px;
}

.landing-input :deep(.v-field) {
    min-height: 60px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 1px 0 rgba(101, 117, 143, 0.08);
}

.landing-input :deep(.v-field__input) {
    font-size: 1rem;
}

.landing-input :deep(.v-label) {
    font-size: 0.98rem;
}

.landing-input :deep(.v-field-label) {
    padding-inline: 4px;
    background: #ffffff;
    opacity: 1;
}

.landing-submit {
    min-height: 60px;
    margin-top: 8px;
    border-radius: 8px !important;
    text-transform: none;
    letter-spacing: 0;
    font-size: 1.08rem !important;
    font-weight: 700;
    box-shadow: 0 18px 34px rgba(13, 94, 219, 0.22);
}

.landing-login-links {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 24px;
    font-size: 0.98rem;
    font-weight: 650;
}

.landing-login-links a {
    color: #045edb;
    text-decoration: none;
}

.landing-capabilities {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0;
    width: min(100% - 48px, 1260px);
    margin: 0 auto;
    padding: 38px 0 46px;
    background: #ffffff;
}

.landing-capability {
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr);
    gap: 24px;
    align-items: start;
    min-height: 128px;
    padding: 12px 38px;
    border-right: 1px solid rgba(101, 117, 143, 0.2);
}

.landing-capability:first-child {
    padding-left: 0;
}

.landing-capability:last-child {
    padding-right: 0;
    border-right: 0;
}

.landing-capability__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    border-radius: 8px;
    background: #eef5ff;
    color: #0969da;
    box-shadow: inset 0 0 0 1px rgba(60, 100, 244, 0.08);
}

.landing-capability__icon :deep(.v-icon) {
    font-size: 38px;
}

.landing-capability__title {
    margin: 4px 0 8px;
    color: #07132e;
    font-size: clamp(1.25rem, 1.65vw, 1.55rem);
    font-weight: 780;
    line-height: 1.2;
    letter-spacing: 0;
}

.landing-capability__body {
    max-width: 340px;
    margin: 0;
    color: #55627b;
    font-size: 1rem;
    line-height: 1.45;
}

.v-theme--dark .smartdata-landing,
.theme--dark .smartdata-landing {
    background: #08111f;
    color: #edf4ff;
}

.v-theme--dark .landing-hero,
.theme--dark .landing-hero {
    border-bottom-color: rgba(140, 179, 255, 0.16);
}

.v-theme--dark .landing-hero__backdrop,
.theme--dark .landing-hero__backdrop {
    background-image:
        linear-gradient(
            90deg,
            rgba(8, 17, 31, 0.98) 0%,
            rgba(8, 17, 31, 0.91) 35%,
            rgba(8, 17, 31, 0.62) 58%,
            rgba(8, 17, 31, 0.28) 100%
        ),
        url('/static/img/landing/smartdata-landing-hero.png');
}

.v-theme--dark .landing-hero__backdrop::after,
.theme--dark .landing-hero__backdrop::after {
    background:
        linear-gradient(180deg, rgba(8, 17, 31, 0) 72%, #08111f 100%),
        repeating-linear-gradient(
            135deg,
            rgba(140, 179, 255, 0.07) 0,
            rgba(140, 179, 255, 0.07) 1px,
            transparent 1px,
            transparent 22px
        );
}

.v-theme--dark .landing-copy__title,
.theme--dark .landing-copy__title,
.v-theme--dark .landing-login-panel__title,
.theme--dark .landing-login-panel__title,
.v-theme--dark .landing-capability__title,
.theme--dark .landing-capability__title {
    color: #f7fbff;
}

.v-theme--dark .landing-copy__body,
.theme--dark .landing-copy__body,
.v-theme--dark .landing-capability__body,
.theme--dark .landing-capability__body {
    color: #b8c6dc;
}

.v-theme--dark .landing-login-panel,
.theme--dark .landing-login-panel {
    border-color: rgba(140, 179, 255, 0.2);
    background: rgba(13, 24, 43, 0.92);
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.35);
}

.v-theme--dark .landing-input :deep(.v-field),
.theme--dark .landing-input :deep(.v-field) {
    background: rgba(255, 255, 255, 0.06);
}

.v-theme--dark .landing-input :deep(.v-field-label),
.theme--dark .landing-input :deep(.v-field-label) {
    background: #0d182b;
}

.v-theme--dark .landing-capabilities,
.theme--dark .landing-capabilities {
    background: #08111f;
}

.v-theme--dark .landing-capability,
.theme--dark .landing-capability {
    border-right-color: rgba(140, 179, 255, 0.16);
}

.v-theme--dark .landing-capability__icon,
.theme--dark .landing-capability__icon {
    background: rgba(140, 179, 255, 0.12);
    color: #8fb9ff;
    box-shadow: inset 0 0 0 1px rgba(140, 179, 255, 0.12);
}

@media (max-width: 1180px) {
    .landing-hero__inner {
        grid-template-columns: 1fr;
        gap: 34px;
        align-items: start;
    }

    .landing-login-panel {
        max-width: 620px;
        margin: 0;
    }

    .landing-capabilities {
        grid-template-columns: 1fr;
        padding-top: 22px;
    }

    .landing-capability,
    .landing-capability:first-child,
    .landing-capability:last-child {
        padding: 24px 0;
        border-right: 0;
        border-bottom: 1px solid rgba(101, 117, 143, 0.2);
    }

    .landing-capability:last-child {
        border-bottom: 0;
    }
}

@media (max-width: 720px) {
    .landing-hero {
        min-height: auto;
    }

    .landing-hero__inner {
        width: min(100% - 32px, 1360px);
        padding: 28px 0 24px;
    }

    .landing-hero__backdrop {
        background-position: 62% center;
    }

    .landing-copy__logo {
        width: min(214px, 72vw);
        margin-bottom: 28px;
    }

    .landing-copy__title {
        font-size: clamp(2.65rem, 13vw, 3.6rem);
    }

    .landing-actions {
        align-items: stretch;
        flex-direction: column;
        gap: 18px;
    }

    .landing-action {
        width: 100%;
    }

    .landing-login-panel {
        padding: 24px 18px;
    }

    .landing-login-links {
        flex-wrap: wrap;
    }

    .landing-capabilities {
        width: min(100% - 32px, 1260px);
    }

    .landing-capability {
        grid-template-columns: 58px minmax(0, 1fr);
        gap: 18px;
    }

    .landing-capability__icon {
        width: 56px;
        height: 56px;
    }

    .landing-capability__icon :deep(.v-icon) {
        font-size: 30px;
    }
}

@media (prefers-reduced-motion: no-preference) {
    .landing-login-panel,
    .landing-copy {
        animation: landing-enter 560ms ease both;
    }

    .landing-login-panel {
        animation-delay: 90ms;
    }
}

@keyframes landing-enter {
    from {
        opacity: 0;
        transform: translateY(14px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
